import { useMemo } from 'react'

import { useQuery } from '@apollo/client'
import { FilterDefinition, FiltersDefinition } from '@factorialco/f0-react/dist/experimental'
import { DocumentsEmployeeFilterDataDocument } from 'generated/resources/graphql'
import { DocumentsSignatureStatusEnumValues } from 'generated/resources/datatable.graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import { useGetFolders } from 'modules/documents/hooks/useGetFolders'
import { SPACES } from 'modules/documents/lib/constants'
import { documentSignatureStatusLabel } from 'modules/documents/lib/Documents'
import { useTeams } from 'modules/employees/hooks/useTeams'
import useLegalEntities from 'modules/employees/hooks/useLegalEntities'
import { useLocations } from 'modules/locations/hooks/useLocations'

const SPACE = SPACES.EMPLOYEE_MY_DOCUMENTS

export type DocumentsFilterKeys =
  | 'createdAt'
  | 'employee'
  | 'employeeStatus'
  | 'folder'
  | 'legalEntity'
  | 'location'
  | 'signatureStatus'
  | 'team'
  | 'visible'

export type DocumentsFiltersDefinition = FiltersDefinition<DocumentsFilterKeys>

export const useFilters = (): {
  filters: DocumentsFiltersDefinition
  loading: boolean
} => {
  const { allMyDescendants, employee: currentEmployee } = useCurrentSession()
  const { folders, loading: loadingFolders } = useGetFolders({ space: SPACE })
  const { teams, loading: loadingTeams } = useTeams()
  const { locations, loading: loadingLocations } = useLocations()
  const { legalEntities, loading: loadingLegalEntities } = useLegalEntities()

  const { data: employeeData, loading: loadingEmployees } = useQuery(
    DocumentsEmployeeFilterDataDocument
  )
  const employees = employeeData?.employees?.employeeNames ?? []

  const loading =
    loadingFolders || loadingTeams || loadingLocations || loadingEmployees || loadingLegalEntities

  const currentEmployeeId = String(currentEmployee?.id ?? '')

  const filters: DocumentsFiltersDefinition = useMemo(() => {
    // Employee status computed groups
    const activeEmployeeIds = employees.filter((e) => !e.terminated).map((e) => e.id)
    const pastEmployeeIds = employees.filter((e) => e.terminated).map((e) => e.id)
    const reportingToMeDirectly = employees
      .filter((e) => e.managerId === currentEmployeeId)
      .map((e) => e.id)
    const reportingToMe = employees.filter((e) => allMyDescendants.includes(e.id)).map((e) => e.id)

    const employeeStatusOptions = [
      {
        value: activeEmployeeIds.join(','),
        label: i18n.t('documents.employee.filters.employee_status_options.active_employees'),
      },
      {
        value: pastEmployeeIds.join(','),
        label: i18n.t('documents.employee.filters.employee_status_options.past_employees'),
      },
      {
        value: reportingToMeDirectly.join(','),
        label: i18n.t('documents.employee.filters.employee_status_options.report_direct'),
      },
      {
        value: reportingToMe.join(','),
        label: i18n.t('documents.employee.filters.employee_status_options.report_me'),
      },
    ].filter(({ value }) => value.length > 0)

    return {
      createdAt: {
        label: i18n.t('documents.employee.list.created_at'),
        type: 'date',
      } satisfies FilterDefinition,

      employee: {
        label: i18n.t('documents.employee.filters.employee'),
        type: 'in',
        options: {
          options: employees
            .map((e) => ({
              label: e.fullName,
              value: e.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        },
      } satisfies FilterDefinition,

      employeeStatus: {
        label: i18n.t('documents.employee.filters.employee_status'),
        type: 'in',
        options: {
          options: employeeStatusOptions,
        },
      } satisfies FilterDefinition,

      folder: {
        label: i18n.t('documents.employee.filters.folder'),
        type: 'in',
        options: {
          options: folders.map((f) => ({
            label: f.name,
            value: f.id,
          })),
        },
      } satisfies FilterDefinition,

      legalEntity: {
        label: i18n.t('documents.employee.filters.legal_entity'),
        type: 'in',
        options: {
          options: legalEntities.map((le) => ({
            label: le.legalName ?? '',
            value: le.id,
          })),
        },
      } satisfies FilterDefinition,

      location: {
        label: i18n.t('documents.employee.filters.location'),
        type: 'in',
        options: {
          options: locations.map((l) => ({
            label: l.name,
            value: l.id,
          })),
        },
      } satisfies FilterDefinition,

      signatureStatus: {
        label: i18n.t('documents.employee.filters.esignature_status'),
        type: 'in',
        options: {
          options: DocumentsSignatureStatusEnumValues.map((status) => ({
            label: documentSignatureStatusLabel(status),
            value: status,
          })),
        },
      } satisfies FilterDefinition,

      team: {
        label: i18n.t('documents.employee.filters.team'),
        type: 'in',
        options: {
          options: teams.map((t) => ({
            label: t.name,
            value: t.id,
          })),
        },
      } satisfies FilterDefinition,

      visible: {
        label: i18n.t('documents.employee.filters.visibility'),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t('documents.employee.filters.visible'),
              value: 'true',
            },
            {
              label: i18n.t('documents.employee.filters.hidden'),
              value: 'false',
            },
          ],
        },
      } satisfies FilterDefinition,
    }
  }, [employees, folders, teams, locations, legalEntities, currentEmployeeId, allMyDescendants])

  return { filters, loading }
}
